/**
 * Create organization → select modules → create project → start the first interview.
 * This is the primary onboarding UX described in docs/plans/initial-build.md §8.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  lookups as lookupsApi,
  organizations as organizationsApi,
  projects as projectsApi,
  questionLibrary,
  sessions as sessionsApi,
} from "../api/resources";
import { Card, ErrorBanner, Field, PageHeader } from "../components/ui";
import { useOrganizations } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";

interface OrganizationForm {
  legal_name: string;
  dba_name: string;
  brand_names: string;
  website: string;
  headquarters_city: string;
  headquarters_region: string;
  headquarters_country: string;
  timezone: string;
  currency: string;
  countries_operated: string;
  industry_code: string;
  business_model: string;
  employee_count_range: string;
}

const EMPTY: OrganizationForm = {
  legal_name: "",
  dba_name: "",
  brand_names: "",
  website: "",
  headquarters_city: "",
  headquarters_region: "",
  headquarters_country: "",
  timezone: "",
  currency: "USD",
  countries_operated: "US",
  industry_code: "",
  business_model: "unknown",
  employee_count_range: "",
};

const BUSINESS_MODELS = [
  "b2b",
  "b2c",
  "b2b2c",
  "dropship",
  "wholesale",
  "marketplace",
  "manufacturer",
  "mixed",
  "unknown",
];

const EMPLOYEE_RANGES = ["1_10", "11_50", "51_200", "201_500", "501_1000", "1000_plus"];

export function OnboardingWizardPage() {
  const navigate = useNavigate();
  const { refresh, select } = useOrganizations();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<OrganizationForm>(EMPTY);
  const [organizationId, setOrganizationId] = useState<string | undefined>();
  const [projectName, setProjectName] = useState("Company onboarding");
  const [selected, setSelected] = useState<string[]>(["company_core"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const lookups = useAsync(() => lookupsApi.all(), []);
  const modules = useAsync(() => questionLibrary.modules(), []);

  const industries = lookups.data?.industries ?? [];
  const organizationModules = useMemo(
    () => (modules.data ?? []).filter((module) => module.applies_to === "organization"),
    [modules.data],
  );
  const peopleModules = useMemo(
    () => (modules.data ?? []).filter((module) => module.applies_to !== "organization"),
    [modules.data],
  );

  const update = (key: keyof OrganizationForm, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const toggleModule = (key: string) =>
    setSelected((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );

  async function createOrganization() {
    setBusy(true);
    setError(undefined);
    try {
      const created = await organizationsApi.create({
        legal_name: form.legal_name.trim(),
        dba_name: form.dba_name.trim() || null,
        brand_names: form.brand_names
          .split(/[\n,]/)
          .map((item) => item.trim())
          .filter(Boolean),
        website: form.website.trim() || null,
        headquarters_city: form.headquarters_city.trim() || null,
        headquarters_region: form.headquarters_region.trim() || null,
        headquarters_country: form.headquarters_country.trim() || null,
        timezone: form.timezone.trim() || null,
        currency: form.currency.trim() || null,
        countries_operated: form.countries_operated
          .split(/[\n,]/)
          .map((item) => item.trim())
          .filter(Boolean),
        industry_code: form.industry_code || null,
        business_model: form.business_model,
        employee_count_range: form.employee_count_range || null,
      });
      setOrganizationId(created.id);
      refresh();
      select(created.id);
      if (created.industry_code === "food_manufacturing" || created.industry_code === "bakery") {
        setSelected((current) =>
          current.includes("food_manufacturing") ? current : [...current, "food_manufacturing"],
        );
      }
      setStep(2);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function createProject() {
    if (!organizationId) return;
    setBusy(true);
    setError(undefined);
    try {
      const project = await projectsApi.create(organizationId, {
        name: projectName.trim() || "Company onboarding",
        selected_modules: selected,
      });
      refresh();
      setStep(3);

      const session = await sessionsApi.create(organizationId, {
        onboarding_project_id: project.id,
        module_keys: selected.filter((key) =>
          organizationModules.some((module) => module.key === key),
        ),
        title: `${form.dba_name || form.legal_name} — onboarding interview`,
      });
      navigate(`/interviews/${session.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Start company onboarding"
        description="Three steps: who the company is, what we need to learn about it, and the first interview. Nothing is filled in on the company's behalf."
      />

      <div className="stack" style={{ maxWidth: 900 }}>
        <ErrorBanner error={error} />

        <Card title={`Step 1 — Organization${step > 1 ? " ✓" : ""}`}>
          {step === 1 ? (
            <>
              <div className="grid grid--two">
                <Field label="Legal company name">
                  <input
                    value={form.legal_name}
                    onChange={(event) => update("legal_name", event.target.value)}
                    placeholder="Golden Crumb Bakery Co."
                  />
                </Field>
                <Field label="DBA / trade name">
                  <input
                    value={form.dba_name}
                    onChange={(event) => update("dba_name", event.target.value)}
                  />
                </Field>
                <Field label="Primary brand names" hint="Comma or newline separated">
                  <input
                    value={form.brand_names}
                    onChange={(event) => update("brand_names", event.target.value)}
                  />
                </Field>
                <Field label="Company website">
                  <input
                    value={form.website}
                    onChange={(event) => update("website", event.target.value)}
                    placeholder="https://"
                  />
                </Field>
                <Field label="Headquarters city">
                  <input
                    value={form.headquarters_city}
                    onChange={(event) => update("headquarters_city", event.target.value)}
                  />
                </Field>
                <Field label="Headquarters region / state">
                  <input
                    value={form.headquarters_region}
                    onChange={(event) => update("headquarters_region", event.target.value)}
                  />
                </Field>
                <Field label="Headquarters country">
                  <input
                    value={form.headquarters_country}
                    onChange={(event) => update("headquarters_country", event.target.value)}
                  />
                </Field>
                <Field label="Primary timezone" hint="Cutoffs and SLAs are quoted in this">
                  <input
                    value={form.timezone}
                    onChange={(event) => update("timezone", event.target.value)}
                    placeholder="America/Los_Angeles"
                  />
                </Field>
                <Field label="Primary currency">
                  <input
                    value={form.currency}
                    onChange={(event) => update("currency", event.target.value)}
                  />
                </Field>
                <Field label="Countries operated in" hint="Comma separated">
                  <input
                    value={form.countries_operated}
                    onChange={(event) => update("countries_operated", event.target.value)}
                  />
                </Field>
                <Field label="Industry">
                  <select
                    value={form.industry_code}
                    onChange={(event) => update("industry_code", event.target.value)}
                  >
                    <option value="">Not selected</option>
                    {industries.map((industry) => (
                      <option key={industry.code} value={industry.code}>
                        {industry.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Business model">
                  <select
                    value={form.business_model}
                    onChange={(event) => update("business_model", event.target.value)}
                  >
                    {BUSINESS_MODELS.map((model) => (
                      <option key={model} value={model}>
                        {model.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Approximate employee count">
                  <select
                    value={form.employee_count_range}
                    onChange={(event) => update("employee_count_range", event.target.value)}
                  >
                    <option value="">Not selected</option>
                    {EMPLOYEE_RANGES.map((range) => (
                      <option key={range} value={range}>
                        {range.replace(/_/g, "–").replace("1000–plus", "1000+")}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <button
                className="primary"
                disabled={busy || form.legal_name.trim().length === 0}
                onClick={() => void createOrganization()}
              >
                Create organization
              </button>
            </>
          ) : (
            <p className="muted">
              {form.legal_name}
              {form.dba_name ? ` (${form.dba_name})` : ""} created. Operations, technical, finance
              and e-commerce contacts are collected in the Company Core interview.
            </p>
          )}
        </Card>

        <Card title="Step 2 — Which modules apply?">
          {step >= 2 ? (
            <>
              <Field label="Onboarding project name">
                <input value={projectName} onChange={(event) => setProjectName(event.target.value)} />
              </Field>

              <h3 style={{ margin: "14px 0 8px" }}>Organization modules</h3>
              <div className="grid grid--two">
                {organizationModules.map((module) => (
                  <label
                    key={module.key}
                    className="row"
                    style={{
                      alignItems: "flex-start",
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      padding: 10,
                      flexWrap: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{ width: 16, marginTop: 3 }}
                      checked={selected.includes(module.key)}
                      onChange={() => toggleModule(module.key)}
                    />
                    <span>
                      <strong>{module.name}</strong>
                      <div className="small muted">{module.description}</div>
                      <div className="tiny faint">
                        {module.question_count} questions
                        {module.industry_scope.length
                          ? ` · ${module.industry_scope.join(", ")}`
                          : ""}
                      </div>
                    </span>
                  </label>
                ))}
              </div>

              <h3 style={{ margin: "18px 0 8px" }}>People &amp; department modules</h3>
              <p className="small muted" style={{ marginTop: 0 }}>
                These are run as separate interviews per department or per person.
              </p>
              <div className="grid grid--two">
                {peopleModules.map((module) => (
                  <label
                    key={module.key}
                    className="row"
                    style={{
                      alignItems: "flex-start",
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      padding: 10,
                      flexWrap: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{ width: 16, marginTop: 3 }}
                      checked={selected.includes(module.key)}
                      onChange={() => toggleModule(module.key)}
                    />
                    <span>
                      <strong>{module.name}</strong>
                      <div className="small muted">{module.description}</div>
                    </span>
                  </label>
                ))}
              </div>

              <div className="row" style={{ marginTop: 16 }}>
                <button
                  className="primary"
                  disabled={busy || selected.length === 0}
                  onClick={() => void createProject()}
                >
                  Create project &amp; start interview
                </button>
                <span className="small muted">{selected.length} modules selected</span>
              </div>
            </>
          ) : (
            <p className="muted">Create the organization first.</p>
          )}
        </Card>

        <Card title="Step 3 — First interview">
          <p className="muted">
            {step === 3
              ? "Opening the interview…"
              : "The interview is assembled from the selected modules and can be paused and resumed at any time."}
          </p>
        </Card>
      </div>
    </>
  );
}
